import type { StylesConfig } from 'react-select';

/** Above Modal backdrop (1300) and content (1301) so portaled menus stay clickable. */
const MENU_Z_INDEX = 2000;

/** Рендер меню в body - не обрезается overflow модалки и скролл-контейнеров. */
export const reactSelectMenuPortalProps = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed' as const,
  menuPlacement: 'auto' as const,
  menuShouldScrollIntoView: true,
};

type AnyStyleFn = (...args: never[]) => object;

function mergeStyleFn(baseFn: AnyStyleFn, overrideFn: AnyStyleFn): AnyStyleFn {
  return (...args: never[]) => ({
    ...baseFn(...args),
    ...overrideFn(...args),
  });
}

/** Базовые стили react-select: ширина под родителя, меню под полем. */
export function mergeSelectStyles<Option, IsMulti extends boolean = false>(
  overrides: StylesConfig<Option, IsMulti> = {},
): StylesConfig<Option, IsMulti> {
  const base: StylesConfig<Option, IsMulti> = {
    container: (provided) => ({
      ...provided,
      width: '100%',
      maxWidth: '100%',
    }),
    control: (provided, state) => ({
      ...provided,
      width: '100%',
      backgroundColor: '#EEEEEE',
      border: '1px solid transparent',
      borderRadius: '15px',
      minHeight: '50px',
      fontSize: '16px',
      fontFamily: "'AA Stetica', sans-serif",
      fontWeight: 400,
      boxShadow: 'none',
      cursor: 'pointer',
      '&:hover': {
        borderColor: 'transparent',
      },
      borderColor: state.isFocused ? '#1E88E5' : 'transparent',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '2px 12px',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      paddingRight: 8,
    }),
    placeholder: (provided) => ({
      ...provided,
      // контраст: #999 на сером поле был 2.5:1; плейсхолдер здесь часто играет роль значения «Все»
      color: '#626b73',
      fontSize: '16px',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#333333',
      fontSize: '16px',
    }),
    input: (provided) => ({
      ...provided,
      color: '#333333',
      fontSize: '16px',
      margin: 0,
      padding: 0,
    }),
    menu: (provided) => ({
      ...provided,
      width: '100%',
      borderRadius: '15px',
      overflow: 'hidden',
      marginTop: 4,
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
      zIndex: MENU_Z_INDEX,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: MENU_Z_INDEX,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 240,
      padding: '4px 0',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#EEEEEE' : state.isFocused ? '#f5f5f5' : 'white',
      color: '#333333',
      fontSize: '16px',
      padding: '12px 16px',
      cursor: 'pointer',
    }),
  };

  const merged = { ...base } as Record<string, unknown>;
  const baseRecord = base as Record<string, unknown>;
  const overrideRecord = overrides as Record<string, unknown>;

  Object.keys(overrideRecord).forEach((key) => {
    const override = overrideRecord[key];
    if (!override) return;
    const baseFn = baseRecord[key];

    merged[key] =
      typeof override === 'function' && typeof baseFn === 'function'
        ? mergeStyleFn(baseFn as AnyStyleFn, override as AnyStyleFn)
        : override;
  });

  return merged as StylesConfig<Option, IsMulti>;
}
